import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { SkeletonModule } from 'primeng/skeleton';
import { DialogModule } from 'primeng/dialog';
import { TabsModule } from 'primeng/tabs';
import { AccountsService } from '../accounts.service';
import { ZatcaOnboardingStatus, BayanOnboardingStatus } from '../accounting-compliance.model';
import { SessionStorageService } from '../../services/session-storage.service';
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';
import { PageHeroComponent } from '../../shared/page-layout/page-hero.component';
import { PageDataPanelComponent } from '../../shared/page-layout/page-data-panel.component';
import { finalize } from 'rxjs';

type GovernmentCountryKey = 'saudi-arabia' | 'uae' | 'qatar' | 'bahrain' | 'oman' | 'kuwait';
type GovernmentLiveStatus = 'zatca' | 'bayan';
type GovernmentActionType = 'open-zatca' | 'open-bayan-dialog' | 'deboard-bayan' | 'coming-soon';

interface GovernmentLogoImage {
    type: 'image';
    src: string;
    alt: string;
}

interface GovernmentLogoPlaceholder {
    type: 'placeholder';
    text: string;
    icon: string;
}

type GovernmentLogo = GovernmentLogoImage | GovernmentLogoPlaceholder;

interface GovernmentActionConfig {
    type: GovernmentActionType;
    label: string;
    icon: string;
    severity?: 'secondary' | 'success' | 'info' | 'warn' | 'danger' | 'contrast';
}

interface GovernmentServiceCard {
    id: string;
    title: string;
    subtitle: string;
    description: string;
    logo: GovernmentLogo;
    action: GovernmentActionConfig;
    statusSource?: GovernmentLiveStatus;
    comingSoon?: boolean;
}

interface GovernmentCountryTab {
    key: GovernmentCountryKey;
    label: string;
    subtitle: string;
    services: GovernmentServiceCard[];
}

@Component({
    selector: 'app-government-onboarding',
    standalone: true,
    imports: [
        CommonModule, FormsModule, RouterModule, ButtonModule, InputTextModule,
        TagModule, ToastModule, SkeletonModule, DialogModule, TabsModule,
        PageShellComponent, PageHeroComponent, PageDataPanelComponent
    ],
    providers: [MessageService],
    templateUrl: './government-onboarding.component.html',
    styleUrls: ['./government-onboarding.component.css']
})
export class GovernmentOnboardingComponent implements OnInit {

    loading = false;
    companyId: number | null = null;
    activeCountry: GovernmentCountryKey = 'saudi-arabia';

    // ZATCA
    zatcaStatus: ZatcaOnboardingStatus | null = null;

    // Bayan
    bayanStatus: BayanOnboardingStatus | null = null;
    bayanLoading = false;
    showBayanDialog = false;
    bayanClientId = '';
    readonly countryTabs: GovernmentCountryTab[] = this.buildCountryTabs();

    constructor(
        private route: ActivatedRoute,
        private accountsService: AccountsService,
        private sessionStorageService: SessionStorageService,
        private messageService: MessageService,
        private router: Router
    ) {}

    ngOnInit(): void {
        const company = this.sessionStorageService.getCompany();
        this.companyId = company?.id ?? null;
        this.route.queryParamMap.subscribe(queryParams => {
            this.activeCountry = this.resolveCountryTab(queryParams.get('tab'));
        });
        if (this.companyId) {
            this.loadStatuses();
        }
    }

    loadStatuses(): void {
        if (!this.companyId) return;
        this.loading = true;

        this.accountsService.getZatcaOnboardingStatus(this.companyId).subscribe({
            next: s => this.zatcaStatus = s,
            error: () => this.zatcaStatus = { onboarded: false }
        });

        this.accountsService.getBayanOnboardingStatus(this.companyId).pipe(
            finalize(() => this.loading = false)
        ).subscribe({
            next: s => this.bayanStatus = s,
            error: () => this.bayanStatus = { onboarded: false }
        });
    }

    openZatca(): void {
        this.router.navigate(['/accounts/enterprise/compliance/zatca-onboarding']);
    }

    openBayanDialog(): void {
        this.bayanClientId = '';
        this.showBayanDialog = true;
    }

    setActiveCountry(value: string | number): void {
        if (typeof value === 'string') {
            this.activeCountry = this.resolveCountryTab(value);
            this.router.navigate([], {
                relativeTo: this.route,
                queryParams: { tab: this.activeCountry },
                queryParamsHandling: 'merge',
                replaceUrl: true
            });
        }
    }

    private resolveCountryTab(tabKey: string | null): GovernmentCountryKey {
        if (!tabKey) {
            return 'saudi-arabia';
        }
        return this.countryTabs.some(tab => tab.key === tabKey)
            ? tabKey as GovernmentCountryKey
            : 'saudi-arabia';
    }

    handleServiceAction(service: GovernmentServiceCard): void {
        if (service.id === 'bayan' && this.isServiceOnboarded(service)) {
            this.deboardBayan();
            return;
        }

        switch (service.action.type) {
            case 'open-zatca':
                this.openZatca();
                break;
            case 'open-bayan-dialog':
                this.openBayanDialog();
                break;
            case 'deboard-bayan':
                this.deboardBayan();
                break;
            default:
                break;
        }
    }

    isServiceOnboarded(service: GovernmentServiceCard): boolean {
        return this.getLiveStatus(service)?.onboarded === true;
    }

    isServicePending(service: GovernmentServiceCard): boolean {
        return !!service.statusSource && this.loading && !this.getLiveStatus(service);
    }

    getServiceTagValue(service: GovernmentServiceCard): string {
        if (service.comingSoon) return 'Coming Soon';
        if (this.isServicePending(service)) return 'Checking';
        return this.isServiceOnboarded(service) ? 'Connected' : 'Not Connected';
    }

    getServiceTagSeverity(service: GovernmentServiceCard): 'success' | 'secondary' | 'info' | 'warn' {
        if (service.comingSoon) return 'warn';
        if (this.isServicePending(service)) return 'info';
        return this.isServiceOnboarded(service) ? 'success' : 'secondary';
    }

    getServiceMeta(service: GovernmentServiceCard): Array<{ icon: string; text: string }> {
        if (service.comingSoon) {
            return [{ icon: 'pi pi-clock', text: 'Coming soon in LogiLink' }];
        }

        if (this.isServicePending(service)) {
            return [{ icon: 'pi pi-spin pi-spinner', text: 'Checking onboarding status' }];
        }

        if (service.statusSource === 'zatca') {
            return this.isServiceOnboarded(service)
                ? [{ icon: 'pi pi-check-circle', text: 'Onboarded' }]
                : [{ icon: 'pi pi-exclamation-circle', text: 'Not onboarded' }];
        }

        if (service.statusSource === 'bayan') {
            if (this.isServiceOnboarded(service) && this.bayanStatus?.clientId) {
                return [{ icon: 'pi pi-id-card', text: `Client: ${this.bayanStatus.clientId}` }];
            }

            return this.isServiceOnboarded(service)
                ? [{ icon: 'pi pi-check-circle', text: 'Connected' }]
                : [{ icon: 'pi pi-exclamation-circle', text: 'Not connected' }];
        }

        return [];
    }

    isActionDisabled(service: GovernmentServiceCard): boolean {
        return service.action.type === 'coming-soon' || this.isServicePending(service);
    }

    isActionLoading(service: GovernmentServiceCard): boolean {
        return service.action.type === 'deboard-bayan' && this.bayanLoading;
    }

    submitBayanOnboarding(): void {
        if (!this.bayanClientId.trim() || !this.companyId) return;
        this.bayanLoading = true;
        this.accountsService.onboardBayan(this.companyId, this.bayanClientId.trim()).pipe(
            finalize(() => this.bayanLoading = false)
        ).subscribe({
            next: res => {
                this.messageService.add({ severity: 'success', summary: 'Connected', detail: res.message || 'Bayan credentials saved.' });
                this.showBayanDialog = false;
                this.loadStatuses();
            },
            error: err => {
                this.messageService.add({ severity: 'error', summary: 'Connection Failed', detail: err.error?.message || err.message });
            }
        });
    }

    deboardBayan(): void {
        if (!this.companyId) return;
        this.bayanLoading = true;
        this.accountsService.deboardBayan(this.companyId).pipe(
            finalize(() => this.bayanLoading = false)
        ).subscribe({
            next: res => {
                this.messageService.add({ severity: 'info', summary: 'Removed', detail: res.message || 'Bayan credentials removed.' });
                this.bayanStatus = { onboarded: false };
            },
            error: err => {
                this.messageService.add({ severity: 'error', summary: 'Remove Failed', detail: err.error?.message || err.message });
            }
        });
    }

    private getLiveStatus(service: GovernmentServiceCard): ZatcaOnboardingStatus | BayanOnboardingStatus | null {
        switch (service.statusSource) {
            case 'zatca':
                return this.zatcaStatus;
            case 'bayan':
                return this.bayanStatus;
            default:
                return null;
        }
    }

    private buildCountryTabs(): GovernmentCountryTab[] {
        return [
            {
                key: 'saudi-arabia',
                label: 'Saudi Arabia',
                subtitle: 'Live onboarding for ZATCA and Bayan, plus planned transport and customs integrations.',
                services: [
                    {
                        id: 'zatca',
                        title: 'ZATCA E-Invoicing',
                        subtitle: 'Zakat, Tax and Customs Authority',
                        description: 'Register and manage compliance for Saudi ZATCA e-invoicing (Fatoora). Generate CSR, submit compliance invoices, and obtain production CSIDs for B2B and B2C invoice clearance and reporting.',
                        logo: {
                            type: 'image',
                            src: 'https://play-lh.googleusercontent.com/zxEN4KlhtogMvDGbWjNxJuoWL-SSJcJYoWK0uStJWLBs6wgYqgcCSoIyzCitPx77upyaJZD98qFa8dF6sunK5g',
                            alt: 'ZATCA'
                        },
                        statusSource: 'zatca',
                        action: { type: 'open-zatca', label: 'Open ZATCA Onboarding', icon: 'pi pi-external-link' }
                    },
                    {
                        id: 'bayan',
                        title: 'Bayan Naql',
                        subtitle: 'Elm - National Transport Portal',
                        description: 'Connect to Bayan (Naql) for government-managed freight waybills. Generate official e-waybills for every trip, ensuring compliance with Saudi transport regulations.',
                        logo: {
                            type: 'image',
                            src: 'https://play-lh.googleusercontent.com/3_DOkzg17McEy6MORWQVFgPo-D8IvYDGXyeXQPno9oZK-e1YJ3Fvk8-2icfXv42x6RDj-f4hF0xUsJqFx3sBbA=w600-h300-pc0xffffff-pd',
                            alt: 'Bayan Naql'
                        },
                        statusSource: 'bayan',
                        action: { type: 'open-bayan-dialog', label: 'Connect Bayan', icon: 'pi pi-link' }
                    },
                    {
                        id: 'fasah',
                        title: 'Fasah Trade & Customs',
                        subtitle: 'Saudi Trade Single Window',
                        description: 'Planned support for customs, import and export workflows, shipment tracking, and trade documentation across Fasah-connected logistics processes.',
                        logo: { type: 'placeholder', text: 'FS', icon: 'pi pi-briefcase' },
                        comingSoon: true,
                        action: { type: 'coming-soon', label: 'Coming Soon', icon: 'pi pi-clock', severity: 'secondary' }
                    },
                    {
                        id: 'wasl',
                        title: 'Wasl Telematics Compliance',
                        subtitle: 'Transport General Authority',
                        description: 'Planned support for vehicle tracking compliance, transport activity verification, and telemetry-based regulatory workflows for land transport operations.',
                        logo: { type: 'placeholder', text: 'WS', icon: 'pi pi-map-marker' },
                        comingSoon: true,
                        action: { type: 'coming-soon', label: 'Coming Soon', icon: 'pi pi-clock', severity: 'secondary' }
                    },
                    {
                        id: 'athr-naql',
                        title: 'Athr Naql Transport Services',
                        subtitle: 'Land Transport Digital Services',
                        description: 'Planned support for digital transport-sector procedures and investor-facing workflows related to Saudi land transport operations.',
                        logo: { type: 'placeholder', text: 'AN', icon: 'pi pi-truck' },
                        comingSoon: true,
                        action: { type: 'coming-soon', label: 'Coming Soon', icon: 'pi pi-clock', severity: 'secondary' }
                    },
                    {
                        id: 'daleel',
                        title: 'Daleel Fleet Monitoring',
                        subtitle: 'Vehicle Signals and Alerts',
                        description: 'Planned support for location, speed, weight, and compliance alert visibility for regulated fleet operations and monitored transport activities.',
                        logo: { type: 'placeholder', text: 'DL', icon: 'pi pi-map' },
                        comingSoon: true,
                        action: { type: 'coming-soon', label: 'Coming Soon', icon: 'pi pi-clock', severity: 'secondary' }
                    }
                ]
            },
            {
                key: 'uae',
                label: 'UAE',
                subtitle: 'Planned customs and tax integration points for trade and invoicing workflows in the UAE.',
                services: [
                    {
                        id: 'mirsal-2',
                        title: 'Dubai Customs Mirsal 2',
                        subtitle: 'Dubai Customs',
                        description: 'Planned support for customs declaration and clearance workflows aligned with Dubai Customs electronic trade services.',
                        logo: { type: 'placeholder', text: 'DC', icon: 'pi pi-globe' },
                        comingSoon: true,
                        action: { type: 'coming-soon', label: 'Coming Soon', icon: 'pi pi-clock', severity: 'secondary' }
                    },
                    {
                        id: 'emaratax',
                        title: 'EmaraTax / UAE E-Invoicing',
                        subtitle: 'Federal Tax Authority',
                        description: 'Planned support for UAE tax platform workflows and future e-invoicing requirements for finance and compliance operations.',
                        logo: { type: 'placeholder', text: 'ET', icon: 'pi pi-file' },
                        comingSoon: true,
                        action: { type: 'coming-soon', label: 'Coming Soon', icon: 'pi pi-clock', severity: 'secondary' }
                    }
                ]
            },
            {
                key: 'qatar',
                label: 'Qatar',
                subtitle: 'Planned customs clearance support for Qatar trade and border operations.',
                services: [
                    {
                        id: 'al-nadeeb',
                        title: 'Al Nadeeb Customs Clearance',
                        subtitle: 'General Authority of Customs',
                        description: 'Planned support for customs declaration, clearance tracking, and cross-border trade workflows through Qatar customs digital services.',
                        logo: { type: 'placeholder', text: 'QN', icon: 'pi pi-list' },
                        comingSoon: true,
                        action: { type: 'coming-soon', label: 'Coming Soon', icon: 'pi pi-clock', severity: 'secondary' }
                    }
                ]
            },
            {
                key: 'bahrain',
                label: 'Bahrain',
                subtitle: 'Planned single-window customs support for Bahrain import and export operations.',
                services: [
                    {
                        id: 'ofoq',
                        title: 'OFOQ Customs Single Window',
                        subtitle: 'Bahrain Customs Affairs',
                        description: 'Planned support for Bahrain customs single-window workflows, border point processing, and trade documentation operations.',
                        logo: { type: 'placeholder', text: 'OF', icon: 'pi pi-briefcase' },
                        comingSoon: true,
                        action: { type: 'coming-soon', label: 'Coming Soon', icon: 'pi pi-clock', severity: 'secondary' }
                    }
                ]
            },
            {
                key: 'oman',
                label: 'Oman',
                subtitle: 'Planned customs single-window support for registration, declarations, and trade processing.',
                services: [
                    {
                        id: 'oman-bayan',
                        title: 'Bayan Electronic Single Window',
                        subtitle: 'Oman Customs',
                        description: 'Planned support for company registration, customs declaration, and electronic single-window trade workflows in Oman.',
                        logo: { type: 'placeholder', text: 'OB', icon: 'pi pi-building' },
                        comingSoon: true,
                        action: { type: 'coming-soon', label: 'Coming Soon', icon: 'pi pi-clock', severity: 'secondary' }
                    }
                ]
            },
            {
                key: 'kuwait',
                label: 'Kuwait',
                subtitle: 'Planned eCustoms support for company onboarding and customs clearance processes.',
                services: [
                    {
                        id: 'kuwait-company-registration',
                        title: 'Kuwait eCustoms Company Registration',
                        subtitle: 'General Administration of Customs',
                        description: 'Planned support for company registration and onboarding into Kuwait customs electronic services as a prerequisite for trade operations.',
                        logo: { type: 'placeholder', text: 'KC', icon: 'pi pi-user-plus' },
                        comingSoon: true,
                        action: { type: 'coming-soon', label: 'Coming Soon', icon: 'pi pi-clock', severity: 'secondary' }
                    },
                    {
                        id: 'kuwait-custom-clearances',
                        title: 'Kuwait Custom Clearances',
                        subtitle: 'Kuwait Government Online',
                        description: 'Planned support for imported-goods customs clearance workflows and related trade processing steps through Kuwait e-services.',
                        logo: { type: 'placeholder', text: 'KW', icon: 'pi pi-send' },
                        comingSoon: true,
                        action: { type: 'coming-soon', label: 'Coming Soon', icon: 'pi pi-clock', severity: 'secondary' }
                    }
                ]
            }
        ];
    }
}
